import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/api';

// OvernightDigestPanel — renders the latest overnight_review snapshot
// (cached by the management command after each successful email send).
// Reads /strategies/api/admin/overnight-digest/

export default function OvernightDigestPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('html'); // 'html' | 'text'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/strategies/api/admin/overnight-digest/');
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        return;
      }
      const j = await res.json();
      setData(j);
      setError('');
    } catch (e) {
      setError('Connection failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--tv-text)' }}>Overnight Digest</h2>
        <span style={{ fontSize: 11, color: 'var(--tv-text-muted)', fontFamily: 'Roboto Mono, monospace' }}>
          {data?.available
            ? `${data.review_date} · cached ${new Date(data.created_at).toLocaleString()}`
            : 'no snapshot available yet'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => setView('html')} style={tabBtnStyle(view === 'html')}>HTML</button>
          <button type="button" onClick={() => setView('text')} style={tabBtnStyle(view === 'text')}>Text</button>
          <button type="button" onClick={load} style={{
            padding: '6px 14px', fontSize: 12, fontWeight: 600,
            border: '1px solid var(--tv-border)', borderRadius: 6, cursor: 'pointer',
            background: 'var(--tv-surface)', color: 'var(--tv-blue)',
          }}>Refresh</button>
        </div>
      </div>

      {loading && (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--tv-text-muted)' }}>Loading…</div>
      )}

      {error && (
        <div style={{ padding: 12, background: 'var(--tv-bear-tint)', border: '1px solid var(--tv-bear)', borderRadius: 6, color: 'var(--tv-bear)', fontSize: 12 }}>
          {error}
        </div>
      )}

      {!loading && !error && !data?.available && (
        <div style={{
          padding: 30, textAlign: 'center', background: 'var(--tv-surface)',
          border: '1px dashed var(--tv-border)', borderRadius: 8,
          color: 'var(--tv-text-muted)', fontSize: 13,
        }}>
          No digest cached yet. The overnight_review command fires at 03:00 ET
          (~02:00 Lagos) on trading days — the next run will populate this view.
        </div>
      )}

      {data?.available && (
        <>
          <div style={{
            padding: '12px 16px', background: 'var(--tv-surface-hi)',
            border: '1px solid var(--tv-border)', borderRadius: 6,
            marginBottom: 12, fontSize: 13, fontWeight: 600, color: 'var(--tv-text)',
          }}>
            {data.subject}
          </div>
          {data.sent_to && (
            <div style={{ fontSize: 11, color: 'var(--tv-text-muted)', marginBottom: 12 }}>
              Sent to: {data.sent_to}
            </div>
          )}

          {view === 'html' && data.body_html ? (
            <div style={{
              background: 'var(--tv-surface)', border: '1px solid var(--tv-border)',
              borderRadius: 8, padding: 0, overflow: 'hidden',
            }}>
              <iframe
                title="Overnight Digest HTML"
                srcDoc={data.body_html}
                style={{ width: '100%', minHeight: 700, border: 'none', background: '#fff' }}
              />
            </div>
          ) : (
            <pre style={{
              background: 'var(--tv-surface)', border: '1px solid var(--tv-border)',
              borderRadius: 8, padding: 16, fontSize: 12, lineHeight: 1.55,
              color: 'var(--tv-text)', fontFamily: 'Roboto Mono, monospace',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              maxHeight: '70vh', overflow: 'auto',
            }}>{data.body_text}</pre>
          )}
        </>
      )}
    </div>
  );
}

function tabBtnStyle(active) {
  return {
    padding: '6px 14px', fontSize: 12, fontWeight: 600,
    border: `1px solid ${active ? 'var(--tv-blue)' : 'var(--tv-border)'}`,
    background: active ? 'var(--tv-blue-tint)' : 'var(--tv-surface)',
    color: active ? 'var(--tv-blue)' : 'var(--tv-text-dim)',
    borderRadius: 6, cursor: 'pointer',
  };
}
